import * as React from 'react';
import { FC, useState, useEffect } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import styled from 'styled-components';
import { CatalogCategoryData, CatalogTemplateData } from '../../../../models/api-models';
import { useTranslations } from '../../../../store/translations/hooks';
import { TK } from '../../../../store/translations/translationKeys';
import AppButton from '../../../../components/AppButton';
import AssignmentIcon from '@material-ui/icons/Assignment';
import EditIcon from '@material-ui/icons/Edit';
import { getIconComponent } from '../../../../shared/documentIconPicker';
import { useReportTemplateCatalog } from '../../../../hooks/useReportTemplateCatalog';

interface Props {
    isOpen: boolean;
    onSelectTemplate: (template: CatalogTemplateData) => void;
    onStartFromScratch: () => void;
    onClose: () => void;
}

const CategorySection = styled.div`
    margin-bottom: var(--spacer-M);
`;

const CategoryHeader = styled.div`
    display: flex;
    align-items: center;
    gap: var(--spacer-XS);
    margin-bottom: var(--spacer-S);
    padding-bottom: var(--spacer-XS);
    border-bottom: var(--border-thin) solid var(--appLigthGrey);
`;

const CategoryLabel = styled.span`
    font-weight: 600;
    font-size: 0.9rem;
    color: var(--text-body);
`;

const TemplatesGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
    gap: var(--spacer-S);
`;

const TemplateCard = styled.button<{ $selected: boolean }>`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: var(--spacer-XS);
    background: ${p => p.$selected ? 'var(--bg-hover-light)' : 'none'};
    border: var(--border-thin) solid ${p => p.$selected ? 'var(--appBlue)' : 'var(--appLigthGrey)'};
    border-radius: 6px;
    padding: var(--spacer-S);
    cursor: pointer;
    text-align: left;
    width: 100%;
    transition: border-color 0.15s, background 0.15s;

    &:hover {
        border-color: var(--appBlue);
        background: var(--bg-hover-light);
    }
`;

const TemplateName = styled.span`
    font-weight: 500;
    font-size: 0.875rem;
    color: var(--text-body);
`;

const TemplateDescription = styled.span`
    font-size: 0.8rem;
    color: var(--text-muted);
    line-height: 1.4;
`;

const ModalBodyScrollable = styled(ModalBody)`
    max-height: 70vh;
    overflow-y: auto;
`;

const LoadingState = styled.div`
    padding: var(--spacer-M);
    text-align: center;
    color: var(--text-muted);
`;

const ReportTemplateCatalogModal: FC<Props> = ({ isOpen, onSelectTemplate, onStartFromScratch, onClose }) => {
    const t = useTranslations();
    const { catalog, loading } = useReportTemplateCatalog();
    const [selectedKey, setSelectedKey] = useState<string | undefined>(undefined);

    useEffect(() => {
        if (isOpen) setSelectedKey(undefined);
    }, [isOpen]);

    const selectedTemplate = React.useMemo(() => {
        if (!selectedKey) return undefined;
        for (const cat of catalog) {
            const found = cat.templates.find(tpl => tpl.key === selectedKey);
            if (found) return found;
        }
        return undefined;
    }, [selectedKey, catalog]);

    const handleConfirm = () => {
        if (selectedTemplate) {
            onSelectTemplate(selectedTemplate);
        }
    };

    return (
        <Modal isOpen={isOpen} toggle={onClose} size="lg">
            <ModalHeader toggle={onClose}>{t(TK.reportTemplateCatalogTitle)}</ModalHeader>
            <ModalBodyScrollable>
                <p style={{ color: 'var(--text-muted)', marginBottom: 'var(--spacer-M)' }}>
                    {t(TK.reportTemplateCatalogDescription)}
                </p>
                {loading && <LoadingState>{t(TK.loading)}</LoadingState>}
                {!loading && catalog.map(category => {
                    const CatIcon = getIconComponent(category.icon) || AssignmentIcon;
                    return (
                    <CategorySection key={category.key}>
                        <CategoryHeader>
                            <CatIcon style={{ color: category.colorCode, fontSize: '1.1rem' }} />
                            <CategoryLabel>{category.name}</CategoryLabel>
                        </CategoryHeader>
                        <TemplatesGrid>
                            {category.templates.map(template => {
                                const TplIcon = getIconComponent(template.icon) || AssignmentIcon;
                                const isSelected = selectedKey === template.key;
                                return (
                                    <TemplateCard
                                        key={template.key}
                                        $selected={isSelected}
                                        onClick={() => setSelectedKey(template.key)}
                                    >
                                        <TplIcon style={{ color: template.colorCode, fontSize: '1.2rem', flexShrink: 0 }} />
                                        <TemplateName>{template.name}</TemplateName>
                                        {template.description && (
                                            <TemplateDescription>{template.description}</TemplateDescription>
                                        )}
                                    </TemplateCard>
                                );
                            })}
                        </TemplatesGrid>
                    </CategorySection>
                    );
                })}
            </ModalBodyScrollable>
            <ModalFooter>
                <AppButton
                    id="catalog-modal-use-template"
                    color="primary"
                    disabled={!selectedTemplate}
                    onClick={handleConfirm}
                >
                    {t(TK.useTemplate)}
                </AppButton>{' '}
                <AppButton
                    id="catalog-modal-scratch"
                    color="secondary"
                    onClick={onStartFromScratch}
                >
                    <EditIcon style={{ fontSize: '1rem', marginRight: 'var(--spacer-XS)' }} />
                    {t(TK.startFromScratch)}
                </AppButton>{' '}
                <AppButton id="catalog-modal-close" color="link" onClick={onClose}>
                    {t(TK.cancel)}
                </AppButton>
            </ModalFooter>
        </Modal>
    );
};

export default ReportTemplateCatalogModal;
